import React, { useState } from "react";
import { FiHeart, FiMail, FiPackage, FiUser } from "react-icons/fi";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/account.scss";
import PageMeta from "./PageMeta";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
};

function Account() {
  const [mode, setMode] = useState("login");
  const [formData, setFormData] = useState(emptyForm);
  const [formMessage, setFormMessage] = useState(null);
  const contact = siteConfig.contact || {};
  const isRegistering = mode === "register";

  const chooseMode = (nextMode) => {
    setMode(nextMode);
    setFormData(emptyForm);
    setFormMessage(null);
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (formMessage) setFormMessage(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isRegistering && formData.password !== formData.confirmPassword) {
      setFormMessage({
        type: "error",
        text: "The passwords do not match. Please try again.",
      });
      return;
    }

    setFormMessage({
      type: "success",
      text: isRegistering
        ? "Demo complete: your details passed local validation. No account was created or stored."
        : "Demo complete: your details passed local validation. No sign-in occurred and nothing was sent.",
    });
    setFormData(emptyForm);
  };

  return (
    <main className="account-page">
      <PageMeta
        title="Account"
        description={`Explore the local account demo for ${siteConfig.brandName}.`}
        noIndex
      />

      <header className="account-hero">
        <div className="account-shell account-hero__inner">
          <p className="account-eyebrow">Your place at the table</p>
          <h1>Welcome in.</h1>
          <p>
            A simple account space designed for smoother shopping and saved
            pantry favorites.
          </p>
        </div>
      </header>

      <section className="account-shell account-layout" aria-label="Account demo">
        <aside className="account-story">
          <p className="account-eyebrow">The pantry, made personal</p>
          <h2>A little easier every time you return.</h2>
          <p>
            This storefront preview shows how a future {siteConfig.brandName}
            account experience could feel, without collecting your information.
          </p>

          <ul className="account-benefits">
            <li>
              <FiHeart aria-hidden="true" />
              <span>
                <strong>Save your favorites</strong>
                Keep a shortlist of pantry staples and gifts.
              </span>
            </li>
            <li>
              <FiPackage aria-hidden="true" />
              <span>
                <strong>Prepare for order history</strong>
                A clear home for future purchases and delivery details.
              </span>
            </li>
            <li>
              <FiMail aria-hidden="true" />
              <span>
                <strong>Stay close to the table</strong>
                Make room for thoughtful product and seasonal notes.
              </span>
            </li>
          </ul>

          <Link className="account-story__link" to="/shop">
            Browse the pantry <span aria-hidden="true">→</span>
          </Link>
        </aside>

        <div className="account-panel">
          <div className="account-panel__icon" aria-hidden="true">
            <FiUser />
          </div>
          <div className="account-toggle" aria-label="Choose an account form">
            <button
              className={mode === "login" ? "is-active" : undefined}
              type="button"
              aria-pressed={mode === "login"}
              onClick={() => chooseMode("login")}
            >
              Sign in
            </button>
            <button
              className={isRegistering ? "is-active" : undefined}
              type="button"
              aria-pressed={isRegistering}
              onClick={() => chooseMode("register")}
            >
              Register
            </button>
          </div>

          <div className="account-demo-notice" role="note">
            <strong>Demo only.</strong> This store has no account backend. Details
            entered here are not authenticated, stored, or sent.
          </div>

          <div className="account-panel__heading">
            <h2>{isRegistering ? "Create a demo account" : "Sign in to your table"}</h2>
            <p>
              {isRegistering
                ? "See the intended registration experience in this local storefront."
                : "Use any valid email format and password to preview the form state."}
            </p>
          </div>

          {formMessage && (
            <div
              className={`account-message account-message--${formMessage.type}`}
              role={formMessage.type === "error" ? "alert" : "status"}
            >
              {formMessage.text}
            </div>
          )}

          <form className="account-form" onSubmit={handleSubmit}>
            {isRegistering && (
              <div className="account-form__row">
                <div className="account-field">
                  <label htmlFor="account-first-name">First name</label>
                  <input
                    id="account-first-name"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={updateField}
                    required
                  />
                </div>
                <div className="account-field">
                  <label htmlFor="account-last-name">Last name</label>
                  <input
                    id="account-last-name"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={updateField}
                    required
                  />
                </div>
              </div>
            )}

            <div className="account-field">
              <label htmlFor="account-email">Email address</label>
              <input
                id="account-email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={updateField}
                required
              />
            </div>

            <div className="account-field">
              <label htmlFor="account-password">Password</label>
              <input
                id="account-password"
                name="password"
                type="password"
                autoComplete={isRegistering ? "new-password" : "current-password"}
                minLength="8"
                value={formData.password}
                onChange={updateField}
                required
              />
              <span>At least 8 characters for this demo.</span>
            </div>

            {isRegistering && (
              <div className="account-field">
                <label htmlFor="account-confirm-password">Confirm password</label>
                <input
                  id="account-confirm-password"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength="8"
                  value={formData.confirmPassword}
                  onChange={updateField}
                  required
                />
              </div>
            )}

            <button className="account-form__submit" type="submit">
              {isRegistering ? "Try registration" : "Try sign in"}
            </button>
          </form>

          <p className="account-panel__switch">
            {isRegistering ? "Already have an account?" : "New to the table?"}{" "}
            <button
              type="button"
              onClick={() => chooseMode(isRegistering ? "login" : "register")}
            >
              {isRegistering ? "Sign in" : "Register"}
            </button>
          </p>

          {contact.email && (
            <p className="account-panel__help">
              Need a hand? <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </p>
          )}
        </div>
      </section>
    </main>
  );
}

export default Account;
